import {Reducer, ThunkAction} from "@reduxjs/toolkit"
import Cookies from "js-cookie"
import State from "ducks/state"
import {RECENT_SEARCHES_COOKIE as COOKIE_KEY} from "../../config/constants"

export type RecentsDuckState = {
    queryIds: string[]
}

type PartialState = Partial<RecentsDuckState>

export const SET_RECENT_SEARCH = "SET_RECENT_SEARCH"

type Action = {
    type: typeof SET_RECENT_SEARCH
    payload: PartialState
}

export const setRecent = (payload: PartialState) =>
    ({
        type: SET_RECENT_SEARCH,
        payload,
    } as const)

export const createRecentQuery =
    (term: string): ThunkAction<void, State, void, Action> =>
    (dispatch, getState) => {
        const {
            recents: {queryIds: oldQueryIds},
            settings: {enableRecentSearches, recentSearchesMaxItems, enrichedSearchEnabled},
        } = getState()

        if (!enableRecentSearches) return
        if (oldQueryIds.includes(term)) return

        const MaxItems = enrichedSearchEnabled ? recentSearchesMaxItems.enriched : recentSearchesMaxItems.simple

        // if there are more than or equal to `MaxItems` already in the cookie, we reduce its size to `MaxItems - 1`
        // the -1 is to make room for the new term. Anything before `startIndex` will be removed from the cookie.
        const startIndex = oldQueryIds.length >= MaxItems ? oldQueryIds.length - (MaxItems - 1) : 0

        const queryIds = oldQueryIds
            .slice(startIndex)
            .concat(term)
            .filter(id => id.trim() !== "")
        const payload = {queryIds}

        dispatch(setRecent(payload))

        Cookies.set(COOKIE_KEY, payload.queryIds)
    }

export const deleteRecentQueries = (): ThunkAction<void, State, void, Action> => (dispatch, getState) => {
    const payload = {...initialState}
    dispatch(setRecent(payload))
    Cookies.remove(COOKIE_KEY)
}

export const updateRecentQueries = (): ThunkAction<void, State, null, Action> => (dispatch, getState) => {
    const {
        settings: {enableRecentSearches},
        request: {cookieDomain},
    } = getState()
    let storedQueries = []
    if (enableRecentSearches) {
        storedQueries = Cookies.getJSON(COOKIE_KEY)
        if (!Array.isArray(storedQueries) || (storedQueries.length > 0 && typeof storedQueries[0] !== "string")) {
            deleteRecentQueries()
            storedQueries = []
        }
    } else {
        Cookies.remove(COOKIE_KEY)
    }
    const queryIds = storedQueries.map(queryId => queryId)
    const payload = {queryIds}
    dispatch(setRecent(payload))
}

const initialState: RecentsDuckState = {
    queryIds: [],
}

const reducer: Reducer<RecentsDuckState, Action> = (state = initialState, action) => {
    switch (action.type) {
        case SET_RECENT_SEARCH:
            return {...state, ...action.payload}
        default:
            return state
    }
}
export default reducer
